import { isSquare } from './board';
import { isFace } from './cards';
import type { CurioTarget } from './ledger/types';
import type {
  Action,
  Face,
  LinkTarget,
  MatchState,
  Side,
  Square,
  TrickTarget,
  WildChoice,
} from './types';

/**
 * Structural parsing of untrusted action payloads. applyAction and actionKey share these
 * definitions, so an action is accepted exactly when its canonical key is legal.
 */

export const ACTION_TYPES = [
  'CHOOSE_STARTER',
  'PASS',
  'CONTINUE',
  'PLAY_LINK',
  'END_CHAIN',
  'PLAY_EPITAPH',
  'RECOVER',
  'CHOOSE_INSCRIPTION',
  'CHOOSE_EPITAPH',
  'CONCEDE',
  'USE_CURIO',
] as const;

export const isSide = (v: unknown): v is Side => v === 'S' || v === 'N';

export function isActionShape(v: unknown): v is Action {
  if (typeof v !== 'object' || v === null) return false;
  const a = v as { type?: unknown; side?: unknown };
  return (ACTION_TYPES as readonly unknown[]).includes(a.type) && isSide(a.side);
}

export type ParsedTarget =
  | { readonly kind: 'to'; readonly to: Square }
  | { readonly kind: 'hops'; readonly hops: Square[] }
  | { readonly kind: 'trick'; readonly trick: TrickTarget };

/** A Trick's target rebuilt from its known fields: a pawn, a face, or nothing. */
function parseTrickTarget(v: unknown): TrickTarget | null {
  if (typeof v !== 'object' || v === null) return null;
  const r = v as { pawnId?: unknown; face?: unknown };
  const out: { pawnId?: string; face?: Face } = {};
  if (r.pawnId !== undefined) {
    const id = actionId(r.pawnId);
    if (!id) return null;
    out.pawnId = id;
  }
  if (r.face !== undefined) {
    if (!isFace(r.face)) return null;
    out.face = { color: r.face.color, number: r.face.number };
  }
  return out;
}

export function parseTarget(t: unknown): ParsedTarget | null {
  if (typeof t !== 'object' || t === null) return null;
  const r = t as { to?: unknown; hops?: unknown; trick?: unknown };
  if (r.trick !== undefined) {
    if (r.to !== undefined || r.hops !== undefined) return null;
    const trick = parseTrickTarget(r.trick);
    return trick ? { kind: 'trick', trick } : null;
  }
  if (r.to !== undefined && r.hops === undefined)
    return isSquare(r.to) ? { kind: 'to', to: r.to } : null;
  if (r.hops !== undefined && r.to === undefined) {
    if (!Array.isArray(r.hops) || r.hops.length === 0 || !r.hops.every(isSquare)) return null;
    return { kind: 'hops', hops: r.hops as Square[] };
  }
  return null;
}

export function parseWild(w: unknown): { face: Face; collateralPawnId: string } | null {
  if (typeof w !== 'object' || w === null) return null;
  const r = w as { face?: unknown; collateralPawnId?: unknown };
  if (!isFace(r.face) || typeof r.collateralPawnId !== 'string') return null;
  return {
    face: { color: r.face.color, number: r.face.number },
    collateralPawnId: r.collateralPawnId,
  };
}

/** Longest id an action may name: card, pawn and Epitaph ids are a few characters long. */
export const MAX_ACTION_ID = 64;
/** Most hops a link may list: a Yellow card hops at most its number of times (§8.5). */
export const MAX_HOPS = 8;

const actionId = (v: unknown): string | null =>
  typeof v === 'string' && v.length > 0 && v.length <= MAX_ACTION_ID ? v : null;

const square = (s: Square): Square => ({ x: s.x, y: s.y });

/**
 * An untrusted action rebuilt from its known fields alone, each checked for its type and size;
 * null when it is not an action. Whatever else it carried (fields the engine would ignore, at
 * any depth) is left behind, so an action stored in a replay is never larger than its meaning.
 */
export function canonicalAction(v: unknown): Action | null {
  if (!isActionShape(v)) return null;
  const a = v as unknown as Record<string, unknown> & { readonly side: Side };
  const side = a.side;
  switch (v.type) {
    case 'PASS':
    case 'CONTINUE':
    case 'END_CHAIN':
    case 'CONCEDE':
      return { type: v.type, side };
    case 'CHOOSE_STARTER':
      return isSide(a.starter) ? { type: 'CHOOSE_STARTER', side, starter: a.starter } : null;
    case 'RECOVER': {
      const cardId = actionId(a.cardId);
      return cardId ? { type: 'RECOVER', side, cardId } : null;
    }
    case 'CHOOSE_INSCRIPTION':
      return typeof a.replace === 'boolean'
        ? { type: 'CHOOSE_INSCRIPTION', side, replace: a.replace }
        : null;
    case 'CHOOSE_EPITAPH': {
      const keepEpitaphId = actionId(a.keepEpitaphId);
      return keepEpitaphId ? { type: 'CHOOSE_EPITAPH', side, keepEpitaphId } : null;
    }
    case 'PLAY_EPITAPH': {
      const pawnId = actionId(a.pawnId);
      if (!pawnId) return null;
      if (a.chain !== undefined && typeof a.chain !== 'boolean') return null;
      const chain = a.chain === true ? { chain: true } : {};
      if (a.epitaphId === undefined) return { type: 'PLAY_EPITAPH', side, pawnId, ...chain };
      const epitaphId = actionId(a.epitaphId);
      return epitaphId ? { type: 'PLAY_EPITAPH', side, pawnId, epitaphId, ...chain } : null;
    }
    case 'USE_CURIO': {
      const uid = actionId(a.uid);
      if (!uid) return null;
      if (a.target === undefined) return { type: 'USE_CURIO', side, uid };
      const target = parseCurioTarget(a.target);
      return target ? { type: 'USE_CURIO', side, uid, target } : null;
    }
    case 'PLAY_LINK': {
      const cardId = actionId(a.cardId);
      const parsed = parseTarget(a.target);
      if (!cardId || !parsed || (parsed.kind === 'hops' && parsed.hops.length > MAX_HOPS))
        return null;
      const target: LinkTarget =
        parsed.kind === 'to'
          ? { to: square(parsed.to) }
          : parsed.kind === 'hops'
            ? { hops: parsed.hops.map(square) }
            : { trick: parsed.trick };
      let pawnId: string | undefined;
      if (a.pawnId !== undefined) {
        const id = actionId(a.pawnId);
        if (!id) return null;
        pawnId = id;
      }
      let wild: WildChoice | undefined;
      if (a.wild !== undefined) {
        const w = parseWild(a.wild);
        const collateralPawnId = w && actionId(w.collateralPawnId);
        if (!w || !collateralPawnId) return null;
        wild = { face: w.face, collateralPawnId };
      }
      return {
        type: 'PLAY_LINK',
        side,
        cardId,
        ...(pawnId !== undefined ? { pawnId } : {}),
        target,
        ...(wild ? { wild } : {}),
      };
    }
  }
}

/** A Curio's target rebuilt from its known fields (pawn, face, square, Epitaph, card). */
export function parseCurioTarget(v: unknown): CurioTarget | null {
  if (typeof v !== 'object' || v === null) return null;
  const r = v as Record<string, unknown>;
  const out: { -readonly [K in keyof CurioTarget]: CurioTarget[K] } = {};
  if (r.pawnId !== undefined) {
    const id = actionId(r.pawnId);
    if (!id) return null;
    out.pawnId = id;
  }
  if (r.face !== undefined) {
    if (!isFace(r.face)) return null;
    out.face = { color: r.face.color, number: r.face.number };
  }
  if (r.to !== undefined) {
    if (!isSquare(r.to)) return null;
    out.to = square(r.to);
  }
  if (r.epitaphId !== undefined) {
    const id = actionId(r.epitaphId);
    if (!id) return null;
    out.epitaphId = id;
  }
  if (r.cardId !== undefined) {
    const id = actionId(r.cardId);
    if (!id) return null;
    out.cardId = id;
  }
  return out;
}

const sqKey = (s: Square): string => `${s.x},${s.y}`;
const j = (v: unknown): string => (v === undefined ? 'undefined' : JSON.stringify(v));

/**
 * A canonical key for an action in a state: two actions with the same key have the same effect.
 * Optional parameters are filled in (the chain pawn on later links, the only held Epitaph).
 */
export function actionKey(s: MatchState, action: Action): string {
  if (!isActionShape(action)) return `INVALID|${j(action)}`;
  const a = action as Action & Record<string, unknown>;
  switch (a.type) {
    case 'PLAY_LINK': {
      const pawnId = a.pawnId !== undefined ? a.pawnId : s.chain?.pawnId;
      const target = parseTarget(a.target);
      const targetKey = !target
        ? `invalid:${j(a.target)}`
        : target.kind === 'to'
          ? `to:${sqKey(target.to)}`
          : target.kind === 'hops'
            ? `hops:${target.hops.map(sqKey).join(';')}`
            : `trick:${j(target.trick.pawnId)}/${target.trick.face ? `${target.trick.face.color}${target.trick.face.number}` : '-'}`;
      let wildKey = '-';
      if (a.wild !== undefined) {
        const w = parseWild(a.wild);
        wildKey = w
          ? `${w.face.color}${w.face.number}/${j(w.collateralPawnId)}`
          : `invalid:${j(a.wild)}`;
      }
      return `PLAY_LINK|${a.side}|${j(a.cardId)}|${j(pawnId)}|${targetKey}|${wildKey}`;
    }
    case 'PLAY_EPITAPH': {
      const held = s.players[a.side].epitaphs;
      const epitaphId =
        a.epitaphId !== undefined ? a.epitaphId : held.length === 1 ? held[0]?.id : undefined;
      return `PLAY_EPITAPH|${a.side}|${j(a.pawnId)}|${j(epitaphId)}${a.chain === true ? '|chain' : ''}`;
    }
    case 'USE_CURIO': {
      const target = a.target === undefined ? undefined : parseCurioTarget(a.target);
      const key = target
        ? [
            target.pawnId ?? '',
            target.face ? `${target.face.color}${target.face.number}` : '',
            target.to ? sqKey(target.to) : '',
            target.epitaphId ?? '',
            target.cardId ?? '',
          ].join('/')
        : j(a.target);
      return `USE_CURIO|${a.side}|${j(a.uid)}|${key}`;
    }
    case 'CHOOSE_STARTER':
      return `CHOOSE_STARTER|${a.side}|${j(a.starter)}`;
    case 'RECOVER':
      return `RECOVER|${a.side}|${j(a.cardId)}`;
    case 'CHOOSE_INSCRIPTION':
      return `CHOOSE_INSCRIPTION|${a.side}|${j(a.replace)}`;
    case 'CHOOSE_EPITAPH':
      return `CHOOSE_EPITAPH|${a.side}|${j(a.keepEpitaphId)}`;
    default:
      return `${a.type}|${a.side}`;
  }
}
